import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import DateLabel from '../../../components/label/DateLabel';
import { TimeLabel } from '../../../components/label/TimeLabel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import DurationLabel from '../../../components/label/DurationLabel';
import moment from 'moment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { ExoCalPopper } from '../../../components/exo/cal/ExoCalPopper';
import { createElement, useState } from 'react';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import HelpIcon from '@mui/icons-material/Help';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Pagination from '../../../components/special/Pagination';
import { ExoSkeleton } from '../../../components/exo/ExoSkeleton';
import ForumIcon from '@mui/icons-material/Forum';
import { NewInteraction } from '../../../components/new/NewInteraction';
import ExoDialog from '../../../components/exo/ExoDialog';
import { t } from 'i18next';
import { API_GET, DBRequest, useConfig } from '../../../api/api';

export const LeadMeetingHistory = ({
  invitations,
  setInvitations,
  onInteractionChange,
  onFeedback,
}) => {
  const config = useConfig();

  const [anchorEl, setAnchorEl] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [openInteraction, setOpenInteraction] = useState(null);

  function handleClick(e, inv) {
    setMeeting(inv.event);
    setAnchorEl(e.currentTarget);
  }

  function handleInteraction(invitation) {
    DBRequest({
      config,
      //path: `event-invitations/${invitation.id}`,
      path: `calendar-events/${invitation.event.id}`,
      method: API_GET,
      onResponse: initInteractionData,
    });
  }

  function initInteractionData(res) {
    const event = res.data;

    const date = moment(event.startDateTime);
    const duration = moment(event.endDateTime).diff(date, 'seconds');

    const interactionData = {
      type: 'meeting',
      title: event.label + ' - ' + t('Protocol'),
      dateTime: date.toISOString(),
      duration: event.wholeday || duration > 86400 ? 0 : duration,
      address: event.location,
      note:
        (event.agenda ? t('Agenda') + event.agenda + '<br/>' : '') +
        (event.internalNote ? t('Internal Note') + event.internalNote : ''),
      participants: event.participants,
      source: { id: event.id },
    };

    setOpenInteraction(interactionData);
  }

  if (!invitations)
    return (
      <Box className="overflow-y-auto max-h-[500px] xl:max-h-[9999px] xl:h-full flex flex-col gap-1">
        <ExoSkeleton size="115px" n={5} />
      </Box>
    );
  return (
    <Box className="flex flex-col gap-1 overflow-y-auto max-h-[500px] md:max-h-[9999px] md:h-full">
      {invitations?.data?.map((inv) => (
        <Invitation
          key={inv.id}
          invitation={inv}
          onClick={handleClick}
          onInteraction={handleInteraction}
        />
      ))}
      <ExoCalPopper
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        data={meeting}
        setEditMode={setEditMode}
        editMode={editMode}
      />
      {invitations?.meta?.lastPage !== 1 && (
        <Pagination data={invitations} setData={setInvitations} isSmall />
      )}
      <ExoDialog
        open={openInteraction}
        onClose={() => setOpenInteraction(null)}
        limitWidth
      >
        <NewInteraction
          startData={openInteraction}
          onClose={() => setOpenInteraction(null)}
          onFeedback={onFeedback}
          onCreate={onInteractionChange}
          onUpdate={onInteractionChange}
          source={openInteraction?.source}
          sourceType={'calendar_event'}
          textBlockType={'lead'}
        />
      </ExoDialog>
    </Box>
  );
};

const Invitation = ({ invitation, onClick, onInteraction }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { event, status } = invitation;
  if (!event) return;
  const { label, wholeDay, startDateTime, endDateTime } = event;

  if (!startDateTime || !endDateTime) return;

  const daysDiff = moment(endDateTime).diff(moment(startDateTime), 'days');

  function handleClick(e) {
    onClick(e, invitation);
  }

  function handleInteractionClick(e) {
    e.stopPropagation();
    onInteraction(invitation);
  }

  const statusMap = {
    0: {
      label: t('Response expected'),
      color: colors.grey[400],
      Icon: RadioButtonUncheckedIcon,
      bgTrans: '40',
      textColor: colors.text,
    },
    1: {
      label: t('Meeting confirmed'),
      color: colors.success,
      Icon: ThumbUpIcon,
      bgTrans: '20',
      textColor: colors.success,
    },
    2: {
      label: t('Meeting canceled'),
      color: colors.error,
      Icon: ThumbDownIcon,
      bgTrans: '20',
      textColor: colors.error,
    },
    3: {
      label: t('Meeting confirmed with reservation'),
      color: colors.warning,
      Icon: HelpIcon,
      bgTrans: '20',
      textColor: colors.warning,
    },
  };

  const statusObj = statusMap[status ?? 0];

  return (
    <Button
      onClick={handleClick}
      sx={{
        bgcolor: colors.card,
        border: `1px solid transparent`,
        display: 'block',
        ':hover': {
          bgcolor: colors.info + 30,
          border: `1px solid ${colors.info}`,
        },
      }}
      className="px-2 py-1"
    >
      <Box className="flex flex-wrap w-full justify-between items-center">
        <Box className="flex items-center gap-1">
          {invitation.protocolsCount == 0 ? (
            <Tooltip title={t('Create Protocol')}>
              <IconButton onClick={(e) => handleInteractionClick(e)}>
                <ForumIcon fontSize="small" color="error" />
              </IconButton>
            </Tooltip>
          ) : (
            <ForumIcon className="m-2" fontSize="small" color="success" />
          )}
          <Typography fontSize="14px" textAlign="left" fontWeight={600}>
            {label}
          </Typography>
        </Box>
        {statusObj && (
          <Typography
            className="flex items-center pl-1 pr-2 py-[2px] rounded-md"
            fontWeight={500}
            sx={{
              fontSize: '11px',
              color: statusObj.textColor,
              bgcolor: statusObj.color + statusObj.bgTrans,
            }}
          >
            {createElement(statusObj.Icon, {
              sx: {
                color: statusObj.color,
                marginRight: '10px',
                fontSize: '13px',
              },
            })}
            {statusObj.label}
          </Typography>
        )}
      </Box>
      <Box className="flex gap-1 pt-2 flex-wrap opacity-70">
        <Typography className="pr-4" fontWeight="500" fontSize="12px">
          <CalendarMonthIcon className="mr-1" sx={{ fontSize: '13px' }} />
          <DateLabel date={startDateTime} />
          {daysDiff > 0 && (
            <>
              {' - '}
              <DateLabel date={endDateTime} />
            </>
          )}
        </Typography>
        {!wholeDay && (
          <Typography className="pr-4" fontWeight="500" fontSize="12px">
            <AccessTimeIcon className="mr-1" sx={{ fontSize: '13px' }} />
            <TimeLabel time={startDateTime} format="HH:mm" />
            {` ${t("o'clock")}`}
          </Typography>
        )}
        {!wholeDay && (
          <Typography className="pr-4" fontWeight="500" fontSize="12px">
            <AvTimerIcon className="mr-1" sx={{ fontSize: '13px' }} />
            <DurationLabel
              startDateTime={startDateTime}
              endDateTime={endDateTime}
            />
          </Typography>
        )}
      </Box>
    </Button>
  );
};
